import { useEffect, useRef, useState } from 'react';

import { useMediaQuery } from '@react-hookz/web';
import classNames from 'classnames';
import { Form } from 'formik';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { AnimationWrapper } from 'src/app/components/Elements/Animation/AnimationWrapper';
import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import MarketingPageSection from 'src/app/components/Elements/MarketingPageSection';
import { useFocusOnFirstFormError } from 'src/app/components/Form/Hooks/useFocusOnFirstFormError';
import TextInput from 'src/app/components/Form/TextInput';
import { getCapsuleAddressMapUrl } from 'src/app/utils';
import { smoothFocus } from 'src/app/utils/smoothFocus';
import { CAPSULE_ICON_STRINGS, IS_MOBILE, LOCALIZED_CONTACT_INFO, LOCATION_DISPLAY_NAME } from 'src/consts';

import { AVAILABLE_LOCALES, ELEMENT_IDS, LOCALE_ID_NAMES, Status, WAITLIST_WRAPPER } from './constants';
import type { PropsFromRedux } from './container';
import {
  AddToWaitList,
  AddToWaitListWrapper,
  ButtonWrapper,
  CityName,
  FormContent,
  HeaderText,
  InputWrapper,
  JoinWaitlistButton,
  Locale,
  LocaleCityInfo,
  LocaleText,
  Marker,
  Phone,
  Skyline,
  SuccessBanner,
  SuccessBannerWrapper,
  SuccessMessage,
  SVGCloseWrapper,
  TextLink,
  WaitlistButtonWrapper,
  WaitListContent,
  WaitListText,
} from './styles';
import styles from './styles.module.scss';
import { ZipcodeChecker } from './ZipcodeChecker';

const { CAPSULE_ADDRESS_LINE_TWO, CAPSULE_ADDRESS_LINE_THREE, CAPSULE_CALL_NUMBER_FORMATTED } = LOCALIZED_CONTACT_INFO;

const LOCALIZED_SKYLINE_IMG_PROPS = {
  DEFAULT: { alt: 'NYC Skyline', src: '/public/NYC.svg' },
  NYC: { alt: 'NYC Skyline', src: '/public/NYC.svg' },
  CHI: { alt: 'Chicago Skyline', src: '/public/CHI.svg' },
  MSP: { alt: 'Minneapolis Skyline', src: '/public/MSP.svg' },
  BOS: { alt: 'Boston Skyline', src: '/public/BOS.svg' },
  AUS: { alt: 'Austin Skyline', src: '/public/AUS.svg' },
  WDC: { alt: 'D.C. Skyline', src: '/public/WDC.svg' },
  LA: { alt: 'L.A. Skyline', src: '/public/LA.svg' },
  MIA: { alt: 'Miami Skyline', src: '/public/MIA.svg' },
  PHL: { alt: 'Philadelphia Skyline', src: '/public/PHL.svg' },
  ATL: { alt: 'Atlanta Skyline', src: '/public/ATL.svg' },
  DEN: { alt: 'Denver Skyline', src: '/public/DEN.svg' },
  NAS: { alt: 'Nashville Skyline', src: '/public/NAS.svg' },
  HOU: { alt: 'Houston Skyline', src: '/public/HOU.svg' },
  TAM: { alt: 'Tampa Skyline', src: '/public/TAM.svg' },
};

const LOCALIZED_SKYLINE_MOBILE_IMG_PROPS = {
  DEFAULT: { alt: 'NYC Skyline', src: '/public/NYC-Mobile.svg' },
  NYC: { alt: 'NYC Skyline', src: '/public/NYC-Mobile.svg' },
  CHI: { alt: 'Chicago Skyline', src: '/public/CHI-Mobile.svg' },
  MSP: { alt: 'Minneapolis Skyline', src: '/public/MSP-Mobile.svg' },
  BOS: { alt: 'Boston Skyline', src: '/public/BOS-Mobile.svg' },
  AUS: { alt: 'Austin Skyline', src: '/public/AUS-Mobile.svg' },
  WDC: { alt: 'D.C. Skyline', src: '/public/WDC-Mobile.svg' },
  LA: { alt: 'L.A. Skyline', src: '/public/LA-Mobile.svg' },
  MIA: { alt: 'Miami Skyline', src: '/public/MIA-Mobile.svg' },
  PHL: { alt: 'Philadelphia Skyline', src: '/public/PHL-Mobile.svg' },
  ATL: { alt: 'Atlanta Skyline', src: '/public/ATL-Mobile.svg' },
  DEN: { alt: 'Denver Skyline', src: '/public/DEN-Mobile.svg' },
  NAS: { alt: 'Nashville Skyline', src: '/public/NAS-Mobile.svg' },
  HOU: { alt: 'Houston Skyline', src: '/public/HOU-Mobile.svg' },
  TAM: { alt: 'Tampa Skyline', src: '/public/TAM-Mobile.svg' },
};

interface Props extends PropsFromRedux {
  isSubmitting: boolean;
  status: string;
  errors: { [key: string]: string };
  isValidating: boolean;
}

export const Renderer = ({
  status,
  isSubmitting,
  errors,
  isValidating,
  resetForm,
  location,
  zipCode,
  setFieldValue,
  dispatchSegmentTrack,
}: Props) => {
  useEffect(() => {
    dispatchSegmentTrack(UNIFIED.viewLocations);
  }, []);

  const [showWaitListForm, setShowWaitListForm] = useState(false);
  const [joinWaitList, setJoinWaitList] = useState(false);
  const sm_down = useMediaQuery('(max-width: 575px)');

  const closeMessage = () => {
    setJoinWaitList(true);
  };

  const resetWaitListForm = () => {
    setJoinWaitList(false);
    resetForm();
  };

  const handleAnimate = () => {
    setShowWaitListForm(true);

    if (IS_MOBILE) {
      setTimeout(() => {
        smoothFocus(WAITLIST_WRAPPER, 100, null);
      }, 250);
    }
  };

  const handleWaitListClick = (zipCode: string) => {
    setFieldValue('zipCode', zipCode);
    setShowWaitListForm(true);
    setTimeout(() => {
      smoothFocus(ELEMENT_IDS.emailField, -120, WAITLIST_WRAPPER);
    }, 250);
  };

  const makeHandleLocationAddressClick = (location: string) => () => {
    dispatchSegmentTrack(UNIFIED.selectLocationAddress, { location });
  };

  const makeHandleLocationPhoneClick = (location: string) => () => {
    dispatchSegmentTrack(UNIFIED.selectLocationPhone, { location });
  };

  useEffect(() => {
    const currentHash = location.hash.split('#')[1];
    if (Object.values(LOCALE_ID_NAMES).includes(currentHash)) {
      setTimeout(() => {
        smoothFocus(currentHash, 10, null);
      }, 600);
    }
  }, [location]);

  const showCity = {};
  AVAILABLE_LOCALES.forEach(city => (showCity[city] = true));

  const formRef = useRef(null);
  useFocusOnFirstFormError(formRef, errors, isSubmitting, isValidating, false);

  return (
    <>
      <MarketingPageSection className={classNames(styles.relative, styles.pageTitle)}>
        <HeaderText>Our locations</HeaderText>
        <div id="scroll-elem" className={classNames(styles.transition)} />
      </MarketingPageSection>

      <MarketingPageSection className={styles.removeSpacing} innerClass={classNames(styles.removeSpacing)}>
        <ZipcodeChecker handleWaitListClick={handleWaitListClick} />
      </MarketingPageSection>

      <MarketingPageSection className={classNames(styles.removeSpacing)} innerClass={classNames(styles.removeSpacing)}>
        {AVAILABLE_LOCALES.map(
          city =>
            showCity[city] && (
              <Locale key={city} id={LOCALE_ID_NAMES[city]}>
                <div>
                  {sm_down ? (
                    <Skyline {...LOCALIZED_SKYLINE_MOBILE_IMG_PROPS[city]} />
                  ) : (
                    <Skyline {...LOCALIZED_SKYLINE_IMG_PROPS[city]} />
                  )}
                </div>
                <LocaleCityInfo>
                  <CityName>{LOCATION_DISPLAY_NAME[city]}</CityName>
                  <LocaleText first onClick={makeHandleLocationAddressClick(LOCATION_DISPLAY_NAME[city])}>
                    <Marker>
                      <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.dark_marker} />
                    </Marker>
                    <TextLink href={getCapsuleAddressMapUrl(city)} target="_blank">
                      {CAPSULE_ADDRESS_LINE_TWO[city]}, {CAPSULE_ADDRESS_LINE_THREE[city]}
                    </TextLink>
                  </LocaleText>
                  <LocaleText onClick={makeHandleLocationPhoneClick(LOCATION_DISPLAY_NAME[city])}>
                    <Phone>
                      <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.phone} />
                    </Phone>
                    <TextLink href={`tel:${CAPSULE_CALL_NUMBER_FORMATTED[city]}`}>
                      {CAPSULE_CALL_NUMBER_FORMATTED[city]}
                    </TextLink>
                  </LocaleText>
                </LocaleCityInfo>
              </Locale>
            )
        )}
      </MarketingPageSection>

      <MarketingPageSection className={classNames(styles.removeSpacing)} innerClass={classNames(styles.removeSpacing)}>
        <AddToWaitListWrapper id={WAITLIST_WRAPPER}>
          <AddToWaitList>
            <WaitListContent>
              <h3>Don't see your city?</h3>
              {status === Status.DEFAULT && (
                <>
                  {showWaitListForm ? (
                    <>
                      <WaitListText>
                        <p>Add your email to our waitlist and&nbsp;</p>
                        <p>we'll let you know when we're in&nbsp;</p>
                        <p>your area.</p>
                      </WaitListText>
                      <FormContent>
                        <Form ref={formRef}>
                          <AnimationWrapper isExpanded={true}>
                            <InputWrapper>
                              <TextInput name="zipCode" type="text" label="Delivery Zip Code" value={zipCode} />
                            </InputWrapper>
                            <InputWrapper>
                              <TextInput id={ELEMENT_IDS.emailField} name="waitlistEmail" type="text" label="Email" />
                            </InputWrapper>
                          </AnimationWrapper>
                          <WaitlistButtonWrapper>
                            <JoinWaitlistButton
                              type="submit"
                              size="medium"
                              style="secondary"
                              fixedWidth
                              disabled={isSubmitting}
                            >
                              Join our Waitlist
                            </JoinWaitlistButton>
                          </WaitlistButtonWrapper>
                        </Form>
                      </FormContent>
                    </>
                  ) : (
                    <WaitlistButtonWrapper>
                      <JoinWaitlistButton
                        type="submit"
                        size="medium"
                        style="secondary"
                        fixedWidth
                        onClick={handleAnimate}
                      >
                        Join our Waitlist
                      </JoinWaitlistButton>
                    </WaitlistButtonWrapper>
                  )}
                </>
              )}

              {status === Status.SUCCESS && !joinWaitList ? (
                <SuccessBannerWrapper>
                  <WaitListText>
                    <p>Add your email to our waitlist and&nbsp;</p>
                    <p>we'll let you know when we're in&nbsp;</p>
                    <p>your area.</p>
                  </WaitListText>
                  <AnimationWrapper isExpanded={true}>
                    <SuccessBanner>
                      <SVGCloseWrapper onClick={closeMessage}>
                        <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.system_nav_close} />
                      </SVGCloseWrapper>
                      <SuccessMessage>
                        <p>We’ll let you know when&nbsp;</p>
                        <p>we’ve expanded to your&nbsp;</p>
                        <p>neighborhood!</p>
                      </SuccessMessage>
                    </SuccessBanner>
                  </AnimationWrapper>
                </SuccessBannerWrapper>
              ) : null}

              {joinWaitList && (
                <AnimationWrapper isExpanded={true}>
                  <ButtonWrapper>
                    <JoinWaitlistButton
                      type="submit"
                      size="medium"
                      style="secondary"
                      onClick={resetWaitListForm}
                      fixedWidth
                    >
                      Join our Waitlist
                    </JoinWaitlistButton>
                  </ButtonWrapper>
                </AnimationWrapper>
              )}
            </WaitListContent>
          </AddToWaitList>
        </AddToWaitListWrapper>
      </MarketingPageSection>
    </>
  );
};
